import { useContext, useEffect, useState } from 'react';
import {
  Configure,
  Hits,
  InstantSearch,
  useHits,
  useInstantSearch,
} from 'react-instantsearch';
import { useIntl } from 'react-intl';

import type { SalesforceLanguageCodeEnum } from '@/types/regionTypes';

import { getUserTitleLabelByValue } from '@/components/FormFields/TitleField';
import { getSwatchFilterItemValues } from '@/components/ListingFilters/components/SwatchFilter/utils';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import type { SelectOption } from '@/components/Select';
import { Typography } from '@/components/Typography';
import type { CountryCode } from '@/graphql/saleor/api';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import type {
  AccountApiError,
  AccountApiResponse,
  GuestNotifyMeRequestPayload,
  NotifyMeParam,
  Product,
} from '@/hooks/useNotifyMe';
import { useNotifyMe } from '@/hooks/useNotifyMe';
import { algoliaSearchClient } from '@/integrations/algolia/client';
import {
  generateWatchDetailPagePath,
  getIndexName,
} from '@/integrations/algolia/helpers';
import type { WatchHit } from '@/integrations/algolia/types';
import { EVENT_NAME, popinTitle } from '@/lib/gtm/const';
import { getLocaleForCountryAndLanguage } from '@/lib/regions';
import type { WithChildren } from '@/lib/types';
import { messages } from '@/messages/notifyMe';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { userState } from '@/states/user';

import type {
  ExtendedNewsLetterSchema,
  ExtendedSubmitHandler,
} from './components/NotifyMeFormState/NotifyMeFormState';
import { NotifyFormState } from './components/NotifyMeFormState/NotifyMeFormState';
import { SubscribedState } from './components/SubscribedState/SubscribedState';

import * as S from './NotifyMeModal.styles';

export interface NotifyMeModalProps {
  isOpen: boolean;
  onClose: () => void;
  sku?: string;
}

export const NotifyMeModal = ({
  isOpen,
  onClose,
  sku = '',
}: NotifyMeModalProps) => {
  const { locale } = useRegionProvider();
  const [isSubscribed, setIsSubscribed] = useState<boolean | undefined>(false);
  const [nameWithSalutation, setNameWithSalutation] = useState<string>('');
  const { formatMessage } = useIntl();

  const [error, setError] = useState<AccountApiError | undefined>(undefined);
  const { submitNotifyMe, submitNotifyMeForGuest } = useNotifyMe();
  const [loading, setLoading] = useState<boolean>(false);
  const { sendEcommerceEventBySkus, sendEvent } = useGoogleAnalytics();

  const handleSubmit: ExtendedSubmitHandler = async (
    params: ExtendedNewsLetterSchema,
  ) => {
    const {
      country,
      currency,
      email,
      firstName,
      firstNameKanji,
      language,
      lastName,
      lastNameKanji,
      newsletterConsent,
      price,
      productName,
      productPath,
      title,
    } = params;

    if (!newsletterConsent) return;
    const _country = country as SelectOption;
    const _language = language as SelectOption;
    const _title = title as SelectOption | null;
    const product: Product = {
      sku: sku,
      price,
      theCurrency: currency,
      productName,
    };
    const countryLanguageSlug = getLocaleForCountryAndLanguage(
      _country.value,
      _language.value as SalesforceLanguageCodeEnum,
    );
    const productLink = [
      process.env.NEXT_PUBLIC_URL,
      countryLanguageSlug,
      productPath,
    ].join('');
    const commonParam: NotifyMeParam = {
      email,
      productLink,
      websiteCountryCode: _country.value as CountryCode,
      products: [product],
      url_country_language_slug: countryLanguageSlug,
    };
    try {
      setLoading(true);
      let res: AccountApiResponse<any>;
      if (userState.isAuthenticated) {
        res = await submitNotifyMe(commonParam);
      } else {
        const param: GuestNotifyMeRequestPayload = {
          ...commonParam,
          languageCode: _language.value as CountryCode,
          salutation: _title?.value ?? '',
          firstName,
          lastName,
          recaptchaResponse: '',
          ...(!!firstNameKanji && { firstNameKanji }),
          ...(!!lastNameKanji && { lastNameKanji }),
        };
        res = await submitNotifyMeForGuest(param);
      }
      setLoading(false);
      if (res.code === 200) {
        sendEcommerceEventBySkus({
          event: EVENT_NAME.NOTIFY_ME,
          skus: [sku],
        });

        setNameWithSalutation(`${getUserTitleLabelByValue(
          formatMessage,
          _country.value as CountryCode,
          _title?.value ?? '',
        )}
        ${firstName} ${lastName}`);
        setIsSubscribed(true);
      } else {
        setError({ code: res.code, msg: res.msg });
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setIsSubscribed(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      sendEvent({
        event: EVENT_NAME.POPIN_VIEW,
        popin_title: popinTitle.notifyMe,
      });
    }
  }, [isOpen]);

  const handleModalClose = () => {
    sendEvent({
      event: EVENT_NAME.POPIN_CLOSE,
      popin_title: popinTitle.notifyMe,
    });

    onClose();
  };

  return (
    <Modal
      data-test-id='NotifyMeModal'
      gutters={true}
      isOpen={isOpen}
      maxWidth='sm'
      onClose={handleModalClose}
    >
      <Modal.Header
        data-test-id='NotifyMeModalHeader'
        divider={true}
        onClose={handleModalClose}
      >
        <S.Title>{formatMessage(messages.title)}</S.Title>
      </Modal.Header>
      <Modal.Body data-test-id='NotifyMeModalBody'>
        {loading ? (
          <Loader data-test-id='NotifyMeModalLoader'></Loader>
        ) : (
          <WatchDetailSearch sku={sku}>
            <LoadingIndicator data-test-id='NotifyMeModalLoadingIndicator'></LoadingIndicator>
            <NoResults data-test-id='NotifyMeModalNoResults'></NoResults>
            <Hits
              data-test-id='NotifyMeModalHits'
              hitComponent={({ hit }: { hit: WatchHit }) => {
                const caseName = getSwatchFilterItemValues(
                  hit.case[locale.code.toUpperCase()],
                ).name;
                const dialColorName = getSwatchFilterItemValues(
                  hit.dial_color[locale.code.toUpperCase()],
                ).name;
                const productPath = generateWatchDetailPagePath(hit);
                return (
                  <div
                    key={hit.objectID}
                    data-test-id={`NotifyMeModalHit-${hit.objectID}`}
                  >
                    {!isSubscribed ? (
                      <NotifyFormState
                        caseMaterial={caseName}
                        data-test-id={`NotifyMeModalNotifyForm-${hit.objectID}`}
                        dialColor={dialColorName}
                        handleSubmit={(data) =>
                          handleSubmit({
                            ...data,
                            currency: hit.currency,
                            price: hit.price,
                            productName:
                              hit.name[locale.code.toUpperCase()] ?? '',
                            productPath,
                          })
                        }
                        productName={hit.name[locale.code.toUpperCase()]}
                      />
                    ) : (
                      <SubscribedState
                        caseMaterial={caseName}
                        data-test-id={`NotifyMeModalSubscribedState-${hit.objectID}`}
                        dialColor={dialColorName}
                        nameWithSalutation={nameWithSalutation}
                        onClose={onClose}
                        productName={hit.name[locale.code.toUpperCase()]}
                      />
                    )}
                    {error && error.msg && (
                      <Typography
                        color='error'
                        data-test-id={`NotifyMeModalError-${hit.objectID}`}
                        margin={2}
                        scale='p3'
                        textAlign='center'
                      >
                        {error.msg}
                      </Typography>
                    )}
                  </div>
                );
              }}
              transformItems={(items) => (items.length ? [items[0]] : [])}
            ></Hits>
          </WatchDetailSearch>
        )}
      </Modal.Body>
    </Modal>
  );
};

interface WatchDetailSearchProp extends WithChildren {
  sku?: string;
}

const LoadingIndicator = () => {
  const { status } = useInstantSearch();

  if (status === 'loading' || status === 'stalled') {
    return <Loader data-test-id='NotifyMeModalLoadingIndicator'></Loader>;
  }
  return null;
};

const NoResults = () => {
  const { status } = useInstantSearch();
  const { hits } = useHits();
  const { formatMessage } = useIntl();
  if (!['loading', 'stalled']?.includes(status) && hits && hits.length === 0) {
    return (
      <Typography
        data-test-id='NotifyMeModalNoResults'
        m={2}
        scale='p2'
        textAlign='center'
      >
        {formatMessage(messages.emptyResults)}
      </Typography>
    );
  }
  return null;
};

const WatchDetailSearch = ({ children, sku }: WatchDetailSearchProp) => {
  const locale = useContext(LocaleContext);
  if (!sku) {
    return <></>;
  }

  return (
    <InstantSearch
      indexName={getIndexName('PRODUCT', locale!)}
      insights={true}
      searchClient={algoliaSearchClient}
    >
      <Configure
        distinct={true}
        facetFilters={`objectID:${sku}`}
        restrictSearchableAttributes={['objectID']}
      />
      {children}
    </InstantSearch>
  );
};

NotifyMeModal.displayName = 'NotifyMeModal';
